import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PaymentIcon from "@mui/icons-material/Payment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import moment from "moment";
import { urls } from "../../business/routes/urls";
import { baseAxios } from "../../services/api";
import { api } from "../../configs";
import "./style.css";

export const PaymentsPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [allCustomers, setAllCustomers] = useState([]); // Все данные с сервера
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalMonthlyCharge, setTotalMonthlyCharge] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recurrentChargeDisabled, setRecurrentChargeDisabled] = useState(0); // 0 = Active (включен), 1 = Inactive (отключен), null = все

  useEffect(() => {
    document.title = "Payments";
    fetchAllPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurrentChargeDisabled]);

  const fetchAllPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build params
      const params = {
        all: true, // Получаем все данные сразу
      };
      
      // Add filter if selected
      if (recurrentChargeDisabled !== null) {
        params.recurrent_charge_disabled = recurrentChargeDisabled;
      }
      
      const response = await baseAxios().get(
        api["storage-v2"]["recurrent-payment"].list,
        { params }
      );

      // Expected format: 
      // With pagination: { items: [...], meta: { total_monthly_charge: "..." } }
      // Without pagination (all=true): { items: [...], total: 2997, total_monthly_charge: "388.81" }
      if (response.data) {
        const data = response.data;
        
        // Extract total_monthly_charge from API response
        // Can be in meta (with pagination) or root level (without pagination)
        const apiTotalMonthlyCharge = 
          data.meta?.total_monthly_charge || 
          data.total_monthly_charge || 
          data.totalMonthlyCharge || 
          data.meta?.totalMonthlyCharge || 
          0;
        
        setTotalMonthlyCharge(parseFloat(apiTotalMonthlyCharge) || 0);
        
        // Get all items
        if (data.items && Array.isArray(data.items)) {
          setAllCustomers(data.items);
          // Total can be in meta or root level
          setTotalRecords(data.meta?.total || data.total || data.items.length);
        } else if (Array.isArray(data)) {
          // Fallback: direct array
          setAllCustomers(data);
          setTotalRecords(data.length);
        } else {
          setAllCustomers([]);
          setTotalRecords(0);
        }
      }
    } catch (err) {
      console.error("Error fetching payments:", err);
      setError(err.message || "Failed to fetch payments data");
      setAllCustomers([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileClick = (profileId) => {
    navigate(`${urls.payments}/${profileId}`);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return moment(dateString).format("MM-DD-YYYY");
    } catch (e) {
      return dateString;
    }
  };

  // Format currency helper
  const formatCurrency = (value) => {
    if (!value && value !== 0) return "-";
    const num = parseFloat(value);
    return isNaN(num) ? value : `$${num.toFixed(2)}`;
  };

  // Filter customers based on search term (client-side filtering)
  const filteredCustomers = allCustomers.filter((customer) => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (customer.Name || "").toLowerCase().includes(searchLower) ||
      (customer.Job_Num || "").toLowerCase().includes(searchLower) ||
      (customer.Office || "").toLowerCase().includes(searchLower) ||
      (customer.Lead?.name || "").toLowerCase().includes(searchLower)
    );
  });

  // Calculate pagination for filtered data
  const totalFiltered = filteredCustomers.length;
  const totalPages = Math.ceil(totalFiltered / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const displayCustomers = filteredCustomers.slice(startIndex, endIndex);

  // Calculate active customers (from all data)
  const activeCustomers = allCustomers.filter((customer) => {
    return customer.Out_of_Storage_Date === null || customer.Out_of_Storage_Date === "-";
  }).length;

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F8F9FA", py: 4 }}>
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
              }}
            >
              <PaymentIcon sx={{ color: "#fff", fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} color="#2B3033">
                Storage Payments
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and monitor all storage payment records
              </Typography>
            </Box>
          </Box>

          {/* Statistics Cards */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <Card
              sx={{
                flex: "1 1 250px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
                boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      Total Monthly Charge
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      ${totalMonthlyCharge.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
            <Card
              sx={{
                flex: "1 1 250px",
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                color: "#fff",
                boxShadow: "0 4px 15px rgba(245, 87, 108, 0.3)",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      Active Customers
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {activeCustomers}
                    </Typography>
                  </Box>
                  <PaymentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
            <Card
              sx={{
                flex: "1 1 250px",
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                color: "#fff",
                boxShadow: "0 4px 15px rgba(79, 172, 254, 0.3)",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                      Total Records
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {totalRecords}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Search Bar and Filters */}
          <Card sx={{ mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                <TextField
                  placeholder="Search by name, job number, office, or lead..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#667eea" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: "1 1 300px",
                    minWidth: "250px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      backgroundColor: "#fff",
                      "&:hover fieldset": {
                        borderColor: "#667eea",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#667eea",
                      },
                    },
                  }}
                />
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel id="recurrent-charge-filter-label">
                    Status
                  </InputLabel>
                  <Select
                    labelId="recurrent-charge-filter-label"
                    id="recurrent-charge-filter"
                    value={recurrentChargeDisabled === null ? "" : recurrentChargeDisabled}
                    label="Status"
                    onChange={(e) => {
                      const value = e.target.value;
                      setRecurrentChargeDisabled(value === "" ? null : value);
                      setPage(1); // Reset to first page when filter changes
                    }}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: "#fff",
                    }}
                  >
                    <MenuItem value={0}>Active</MenuItem>
                    <MenuItem value={1}>Inactive</MenuItem>
                    <MenuItem value="">All</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Card
          sx={{
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "400px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer 
                  component={Paper} 
                  elevation={0}
                  sx={{ 
                    maxHeight: "calc(100vh - 300px)",
                    overflowX: "auto"
                  }}
                >
                  <Table 
                    stickyHeader
                    sx={{
                      "& .MuiTableCell-root": {
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow
                        sx={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          "& .MuiTableCell-head": {
                            color: "#fff !important",
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            backgroundColor: "transparent",
                            borderBottom: "2px solid rgba(255, 255, 255, 0.2)",
                            padding: "16px 12px",
                          },
                        }}
                      >
                        <TableCell sx={{ minWidth: 60, textAlign: "center", position: "sticky", left: 0, zIndex: 11, backgroundColor: "#667eea" }}>
                          ID
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          Job Num
                        </TableCell>
                        <TableCell sx={{ minWidth: 200, fontWeight: 700 }}>
                          Name
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          Office
                        </TableCell>
                        <TableCell sx={{ minWidth: 100 }}>
                          First Charge
                        </TableCell>
                        <TableCell sx={{ minWidth: 130 }}>
                          Into Storage Date
                        </TableCell>
                        <TableCell sx={{ minWidth: 130 }}>
                          Out of Storage Date
                        </TableCell>
                        <TableCell sx={{ minWidth: 100 }}>
                          Move Size
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          Monthly Charge
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          Test Payment Success
                        </TableCell>
                        <TableCell sx={{ minWidth: 130 }}>
                          Created Time
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          Lead Name
                        </TableCell>
                        <TableCell sx={{ minWidth: 130 }}>
                          Next Charge Date
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          Late Payment Fee
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          Failed Charge Attempts
                        </TableCell>
                        <TableCell sx={{ minWidth: 80 }}>
                          Tax
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          First Month Free
                        </TableCell>
                        <TableCell sx={{ minWidth: 100 }}>
                          Last Charge
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          Payment Log
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          Log Number
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayCustomers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={20} align="center" sx={{ py: 4 }}>
                            <Typography color="textSecondary">
                              {searchTerm ? "No payments found matching your search" : "No payments found"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        displayCustomers.map((customer, index) => {
                          const customerId = customer.id || customer.zoho_id || index;
                          // Calculate row number considering pagination
                          const rowNumber = startIndex + index + 1;
                          return (
                            <TableRow
                              key={customerId}
                              hover
                              sx={{
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  backgroundColor: "#f5f7ff",
                                  transform: "scale(1.001)",
                                  boxShadow: "0 2px 8px rgba(102, 126, 234, 0.1)",
                                },
                                "&:nth-of-type(even)": {
                                  backgroundColor: "#fafbfc",
                                },
                              }}
                              onClick={() => handleProfileClick(customerId)}
                            >
                              <TableCell sx={{ textAlign: "center", fontWeight: 500 }}>
                                {rowNumber}
                              </TableCell>
                              <TableCell
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleProfileClick(customerId);
                                }}
                                sx={{
                                  color: "#667eea",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  "&:hover": {
                                    color: "#764ba2",
                                    textDecoration: "underline",
                                  },
                                }}
                              >
                                {customer.Job_Num || "-"}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 500 }}>{customer.Name || "-"}</TableCell>
                              <TableCell>
                                <Chip
                                  label={customer.Office || "-"}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#e8eaf6",
                                    color: "#667eea",
                                    fontWeight: 500,
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600, color: "#2e7d32" }}>
                                {formatCurrency(customer.First_Charge)}
                              </TableCell>
                              <TableCell>{formatDate(customer.Into_Storage_Date)}</TableCell>
                              <TableCell>{formatDate(customer.Out_of_Storage_Date)}</TableCell>
                              <TableCell>{customer.Move_Size || "-"}</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: "#1976d2" }}>
                                {formatCurrency(customer.Monthly_Charge)}
                              </TableCell>
                              <TableCell>
                                {customer.TestPaymentSucces !== null && customer.TestPaymentSucces !== undefined
                                  ? (
                                    <Chip
                                      label={customer.TestPaymentSucces ? "Yes" : "No"}
                                      size="small"
                                      color={customer.TestPaymentSucces ? "success" : "default"}
                                    />
                                  )
                                  : "-"}
                              </TableCell>
                              <TableCell>{formatDate(customer.Created_Time)}</TableCell>
                              <TableCell>{customer.Lead?.name || "-"}</TableCell>
                              <TableCell>{formatDate(customer.Next_Charge_Date)}</TableCell>
                              <TableCell>{formatCurrency(customer.Late_Payment_Fee)}</TableCell>
                              <TableCell>
                                {customer.Failed_Charge_Attempts !== null && customer.Failed_Charge_Attempts !== undefined
                                  ? (
                                    <Chip
                                      label={customer.Failed_Charge_Attempts}
                                      size="small"
                                      color={customer.Failed_Charge_Attempts > 0 ? "error" : "default"}
                                    />
                                  )
                                  : "-"}
                              </TableCell>
                              <TableCell>{customer.Tax ? `${customer.Tax}%` : "-"}</TableCell>
                              <TableCell>
                                {customer.First_Month_Free !== null && customer.First_Month_Free !== undefined
                                  ? (
                                    <Chip
                                      label={customer.First_Month_Free ? "Yes" : "No"}
                                      size="small"
                                      color={customer.First_Month_Free ? "success" : "default"}
                                    />
                                  )
                                  : "-"}
                              </TableCell>
                              <TableCell>{formatCurrency(customer.Last_Charge)}</TableCell>
                              <TableCell>{customer.Payment_Log || "-"}</TableCell>
                              <TableCell>{customer.Log_Number || "-"}</TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {totalPages > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 2,
                      px: 3,
                      borderTop: "1px solid #e0e0e0",
                      backgroundColor: "#fafbfc",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {startIndex + 1} to {Math.min(endIndex, totalFiltered)} of {totalFiltered} entries
                      </Typography>
                      <TextField
                        select
                        size="small"
                        value={rowsPerPage}
                        onChange={handleChangeRowsPerPage}
                        sx={{ minWidth: 80 }}
                        SelectProps={{
                          native: true,
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </TextField>
                      <Typography variant="body2" color="text.secondary">
                        per page
                      </Typography>
                    </Box>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handleChangePage}
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          "&.Mui-selected": {
                            backgroundColor: "#667eea",
                            color: "#fff",
                            "&:hover": {
                              backgroundColor: "#764ba2",
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

